import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

/**
 * Global, zero-config table search — mounted once in the Layout.
 *
 * On every page it scans the page-content area for a data table and shows a
 * search box that filters that table's rows by text. It deliberately HIDES
 * itself when:
 *   - the page has no table (dashboards, forms, detail pages), or
 *   - the page already provides its own search input (so there's never a
 *     duplicate search box — the page's own, data-level search is kept).
 *
 * Filtering is DOM-level (toggles each `<tbody>` row's `hidden` attribute), so
 * it works on any table without per-page wiring. A childList MutationObserver
 * re-applies the active filter when rows change (refetch / sort / pagination)
 * and re-evaluates visibility as tables/search inputs mount and unmount.
 * Rows tagged `data-no-filter` (totals / footers) are never hidden.
 */
const CONTENT_SELECTOR = '[data-page-content]';

function getContentRoot(): HTMLElement {
  return document.querySelector<HTMLElement>(CONTENT_SELECTOR) ?? document.body;
}

export default function GlobalTableSearch() {
  const { t } = useTranslation();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(false);
  const queryRef = useRef('');
  queryRef.current = query;

  const getTable = useCallback((): HTMLTableElement | null => {
    const tables = Array.from(getContentRoot().querySelectorAll('table'));
    return (tables.find(tb => tb.querySelector('tbody tr')) ?? tables[0] ?? null) as HTMLTableElement | null;
  }, []);

  const pageHasOwnSearch = useCallback((): boolean => {
    return Array.from(getContentRoot().querySelectorAll('input')).some(inp => {
      const type = (inp.getAttribute('type') ?? 'text').toLowerCase();
      const ph = (inp.getAttribute('placeholder') ?? '').toLowerCase();
      return (type === 'text' || type === 'search') && /search|بحث|ابحث/.test(ph);
    });
  }, []);

  const applyFilter = useCallback((q: string) => {
    const table = getTable();
    if (!table) return;
    const term = q.trim().toLowerCase();
    table.querySelectorAll<HTMLTableRowElement>('tbody tr').forEach(tr => {
      if (tr.hasAttribute('data-no-filter')) { tr.hidden = false; return; }
      tr.hidden = !!term && !(tr.textContent ?? '').toLowerCase().includes(term);
    });
  }, [getTable]);

  // Re-evaluate visibility + re-apply the filter; debounced; observe row changes.
  // NB: we observe childList only (not attributes), so our own `hidden` toggles
  // never re-trigger the observer.
  useEffect(() => {
    let raf = 0;
    const recompute = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const show = !!getTable() && !pageHasOwnSearch();
        setVisible(show);
        if (show) applyFilter(queryRef.current);
      });
    };
    recompute();
    const obs = new MutationObserver(recompute);
    obs.observe(getContentRoot(), { childList: true, subtree: true });
    return () => { obs.disconnect(); cancelAnimationFrame(raf); };
  }, [location.pathname, getTable, pageHasOwnSearch, applyFilter]);

  // Reset the query whenever the route changes.
  useEffect(() => { setQuery(''); }, [location.pathname]);

  // Apply on each keystroke.
  useEffect(() => { if (visible) applyFilter(query); }, [query, visible, applyFilter]);

  if (!visible) return null;

  return (
    <div className="mb-4 flex justify-end">
      <div className="relative w-full sm:w-80">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 dark:text-slate-500 pointer-events-none" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={`${t('common_search', 'Search')}…`}
          aria-label={t('common_search', 'Search')}
          className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 rounded-lg ps-9 pe-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
        />
        {query && (
          <button onClick={() => setQuery('')} className="absolute end-2 top-1/2 -translate-y-1/2 text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white" aria-label="Clear">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
